import React, {useEffect, useState} from 'react'
import styled from 'styled-components'
import TranslationForm from 'organization/Event/LocalizationConfig/TranslationForm'
import {Language} from 'Event/LanguageProvider/language'
import LanguageSelect from 'organization/Event/LocalizationConfig/LanguageSelect'
import {useLanguage} from 'Event/LanguageProvider'
import {Translations} from 'Event/LanguageProvider/translations'
import MakeDefaultButton from 'organization/Event/LocalizationConfig/MakeDefaultButton'
import Box from '@material-ui/core/Box'
import grey from '@material-ui/core/colors/grey'
import AddLanguageButton from 'organization/Event/LocalizationConfig/AddLanguageButton'
import EnableTranslationSwitch from 'organization/Event/LocalizationConfig/EnableTranslationSwitch'
import RemoveLanguageButton from 'organization/Event/LocalizationConfig/RemoveLanguageButton'
import SetRulesButton from 'organization/Event/LocalizationConfig/SetRulesButton'
import LocalizationExport from 'organization/Event/LocalizationConfig/LocalizationExport'
import LocalizationImport from 'organization/Event/LocalizationConfig/LocalizationImport'
import {FeaturesBreadcrumbs} from 'organization/Event/Page/PageBreadcrumbs'
import {useTemplate} from 'Event/TemplateProvider'
import {REMOVE, useSaveTemplate} from 'Event/TemplateUpdateProvider'
import AdvancedModeButton from './AdvancedModeButton'

type LocalizationConfigContextProps = {
  isProcessing: boolean
  setIsProcessing: (processing: boolean) => void
}

const LocalizationConfigContext = React.createContext<
  LocalizationConfigContextProps | undefined
>(undefined)

export default function LocalizationConfig() {
  const {defaultLanguage, isAdvancedMode, translationsEnabled} = useLanguage()
  const [language, setLanguage] = useState<Language['name']>(defaultLanguage)
  const [translations, setTranslations] = useState<Translations | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const remove = useRemoveLanguage()
  const template = useTemplate()

  useEffect(() => {
    const saved = template.localization?.translations

    const translations = saved || {}
    setTranslations(translations)
  }, [template])

  useEffect(() => {
    setLanguage(defaultLanguage)
  }, [defaultLanguage])

  if (!translations) {
    return null
  }

  const canRemove = language !== defaultLanguage && !isProcessing

  const handleRemove = () => {
    if (isProcessing) {
      return
    }
    remove(language)
    setLanguage(defaultLanguage)
  }

  return (
    <FeaturesBreadcrumbs page="Translations">
      <LocalizationConfigContext.Provider
        value={{isProcessing, setIsProcessing}}
      >
        <ConfigBar>
          <LeftItems>
            <EnableTranslationSwitch />
            <AdvancedModeButton />
          </LeftItems>
        </ConfigBar>
        <Body>
          <Box mb={2} display="flex" justifyContent="space-between">
            <ButtonsBox>
              <AddLanguageButton onAdd={setLanguage} />
              <SetRulesButton language={language} />
              <MakeDefaultButton
                language={language}
                translations={translations}
              />
              <RemoveLanguageButton
                onRemove={handleRemove}
                canRemove={canRemove}
                language={language}
              />
            </ButtonsBox>
            <ButtonsBox>
              {isAdvancedMode && (
                <>
                  <LocalizationExport />
                  <LocalizationImport setTranslations={setTranslations} />
                </>
              )}
            </ButtonsBox>
          </Box>
          <LanguageSelect
            value={language}
            onChange={setLanguage}
            showDefault
            disabled={isProcessing}
          />
          {isAdvancedMode && translationsEnabled && (
            <TranslationForm
              language={language}
              translations={translations}
              setTranslations={setTranslations}
            />
          )}
        </Body>
      </LocalizationConfigContext.Provider>
    </FeaturesBreadcrumbs>
  )
}

export function useLocalizationConfig() {
  const context = React.useContext(LocalizationConfigContext)
  if (context === undefined) {
    throw new Error(
      'useLocalizationConfig must be used within LocalizationConfig',
    )
  }

  return context
}

const ButtonsBox = styled.div`
  margin: ${(props) => `-${props.theme.spacing[1]}`}!important;

  > button {
    margin: ${(props) => props.theme.spacing[1]}!important;
  }
  display: flex;
`

function useRemoveLanguage() {
  const {languages} = useLanguage()
  const saveTemplate = useSaveTemplate()

  return (language: Language['name']) => {
    const removedLanguage = languages.filter((l) => l.name !== language)

    return saveTemplate({
      localization: {
        translations: {
          [language]: REMOVE,
        },
        languages: removedLanguage,
      },
    })
  }
}

const ConfigBar = styled.div`
  position: sticky !important;
  top: 0 !important;
  height: 50px !important;
  border-bottom: 1px solid ${grey[300]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${(props) => props.theme.spacing[4]};
  background: #ffffff;
  z-index: 50;
`

const LeftItems = styled.div`
  display: flex;
  align-items: center;
`

const Body = styled.div`
  top: auto !important;
  padding: 20px 60px;
`
